import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BarChartOutlined,
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  GlobalOutlined,
  LineChartOutlined,
  MessageOutlined,
  PieChartOutlined,
  RiseOutlined,
  SoundOutlined,
  TagsOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Card,
  Col,
  DatePicker,
  Empty,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { textLocaleTag } from '../i18n/locale';
import { useAuthStore } from '../store/authStore';
import { formatPercent } from '../utils/formatPercent';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const Analytics: React.FC = () => {
  const { user } = useAuthStore();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>({
    overview: {},
    eventMetrics: [],
    userMetrics: {},
    ticketMetrics: {},
    engagementMetrics: {},
    chatMetrics: {},
  });
  const [trends, setTrends] = useState<any[]>([]);

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs(),
  ]);
  const [view, setView] = useState<'overview' | 'events' | 'tickets' | 'engagement'>('overview');
  const [period, setPeriod] = useState<string>('30');

  useEffect(() => {
    fetchAnalytics();
    fetchTrends();
  }, [dateRange, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/analytics', {
        params: {
          startDate: dateRange[0].toISOString(),
          endDate: dateRange[1].toISOString(),
        },
      });
      setData(response.data);
    } catch (error: any) {
      console.error('Failed to fetch analytics:', error);
      setError(t('analytics.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const response = await api.get('/analytics/trends', {
        params: { period: `${period}d` },
      });
      setTrends(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(textLocaleTag(i18n.language), {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const eventColumns: ColumnsType<any> = [
    {
      title: t('common.event'),
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => <a href={`/events/${record.id}`}>{text}</a>,
    },
    {
      title: t('common.date'),
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('YYYY.MM.DD'),
    },
    {
      title: t('analytics.attendees'),
      dataIndex: 'attendees',
      key: 'attendees',
      sorter: (a, b) => a.attendees - b.attendees,
      render: (value) => (
        <Space>
          <UserOutlined />
          <Text>{value}</Text>
        </Space>
      ),
    },
    {
      title: t('analytics.revenue'),
      dataIndex: 'revenue',
      key: 'revenue',
      sorter: (a, b) => a.revenue - b.revenue,
      render: (value) => <Text strong>{formatCurrency(value)}</Text>,
    },
    {
      title: t('analytics.checkInRate'),
      dataIndex: 'checkInRate',
      key: 'checkInRate',
      render: (value) => (
        <Progress
          percent={value}
          size="small"
          format={() => formatPercent(value, i18n.language)}
          strokeColor={value > 90 ? '#52c41a' : value > 75 ? '#faad14' : '#f5222d'}
        />
      ),
    },
    {
      title: t('analytics.capacity'),
      key: 'capacity',
      render: (_, record) => {
        const utilization =
          record.capacity > 0 ? Math.round((record.ticketsSold / record.capacity) * 100) : 0;
        return (
          <Space>
            <Text>
              {record.ticketsSold} / {record.capacity || '∞'}
            </Text>
            {record.capacity > 0 && (
              <Tag color={utilization > 90 ? 'red' : utilization > 70 ? 'orange' : 'green'}>
                {utilization}%
              </Tag>
            )}
          </Space>
        );
      },
    },
  ];

  const ticketTypeColumns: ColumnsType<any> = [
    {
      title: t('analytics.ticketType'),
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: t('analytics.count'),
      dataIndex: 'count',
      key: 'count',
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: t('analytics.ratio'),
      dataIndex: 'percentage',
      key: 'percentage',
      render: (value) => <Progress percent={value} size="small" strokeColor="#1890ff" />,
    },
    {
      title: t('analytics.revenue'),
      dataIndex: 'revenue',
      key: 'revenue',
      render: (value) => <Text strong>{formatCurrency(value)}</Text>,
    },
  ];

  if (error) {
    return <Alert message={t('analytics.errorTitle')} description={error} type="error" showIcon />;
  }

  const checkInRatio =
    data.overview?.soldTickets > 0 ? data.overview.checkedInTickets / data.overview.soldTickets : 0;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <BarChartOutlined /> {t('analytics.title')}
          </Title>
          <Text type="secondary">{t('analytics.subtitle')}</Text>
        </Col>
        <Col>
          <Space>
            <Select value={period} onChange={setPeriod} style={{ width: 120 }}>
              <Select.Option value="7">{t('analytics.periodDays', { days: 7 })}</Select.Option>
              <Select.Option value="30">{t('analytics.periodDays', { days: 30 })}</Select.Option>
              <Select.Option value="90">{t('analytics.periodDays', { days: 90 })}</Select.Option>
            </Select>
            <RangePicker
              value={dateRange}
              onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
              format="YYYY.MM.DD"
            />
            <Segmented
              value={view}
              onChange={(value) => setView(value as any)}
              options={[
                { label: t('analytics.viewOverview'), value: 'overview' },
                { label: t('analytics.viewEvents'), value: 'events' },
                { label: t('analytics.viewTickets'), value: 'tickets' },
                { label: t('analytics.viewEngagement'), value: 'engagement' },
              ]}
            />
          </Space>
        </Col>
      </Row>

      {loading ? (
        <div
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <>
          {view === 'overview' && (
            <>
              {/* Main Metrics */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title={t('analytics.totalEvents')}
                      value={data.overview?.totalEvents || 0}
                      prefix={<CalendarOutlined />}
                      suffix={
                        data.overview?.eventsGrowth !== undefined && (
                          <Text
                            type={data.overview.eventsGrowth > 0 ? 'success' : 'danger'}
                            style={{ fontSize: 14 }}
                          >
                            {data.overview.eventsGrowth > 0 ? (
                              <ArrowUpOutlined />
                            ) : (
                              <ArrowDownOutlined />
                            )}
                            {Math.abs(data.overview.eventsGrowth)}%
                          </Text>
                        )
                      }
                    />
                    <Space style={{ marginTop: 8 }}>
                      <Tag color="green">
                        {t('analytics.activeTag', { count: data.overview?.activeEvents || 0 })}
                      </Tag>
                      <Tag color="blue">
                        {t('analytics.upcomingTag', { count: data.overview?.upcomingEvents || 0 })}
                      </Tag>
                    </Space>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title={t('analytics.totalAttendees')}
                      value={data.userMetrics?.attendees || 0}
                      prefix={<TeamOutlined />}
                      suffix={
                        data.overview?.attendeesGrowth !== undefined && (
                          <Text
                            type={data.overview.attendeesGrowth > 0 ? 'success' : 'danger'}
                            style={{ fontSize: 14 }}
                          >
                            {data.overview.attendeesGrowth > 0 ? (
                              <ArrowUpOutlined />
                            ) : (
                              <ArrowDownOutlined />
                            )}
                            {Math.abs(data.overview.attendeesGrowth)}%
                          </Text>
                        )
                      }
                    />
                    <Progress
                      percent={
                        data.userMetrics?.total > 0
                          ? Math.round((data.userMetrics.attendees / data.userMetrics.total) * 100)
                          : 0
                      }
                      strokeColor="#52c41a"
                      showInfo={false}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title={t('analytics.totalRevenue')}
                      value={formatCurrency(data.overview?.totalRevenue || 0)}
                      prefix={<DollarOutlined />}
                      valueStyle={{ fontSize: 20 }}
                    />
                    <Text type="secondary">
                      {t('analytics.soldTicketsHint', { count: data.overview?.soldTickets || 0 })}
                    </Text>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title={t('analytics.checkInRate')}
                      value={formatPercent(checkInRatio * 100, i18n.language)}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: checkInRatio > 0.8 ? '#52c41a' : '#faad14' }}
                    />
                    <Text type="secondary">
                      {t('analytics.checkedInHint', {
                        checked: data.overview?.checkedInTickets || 0,
                        sold: data.overview?.soldTickets || 0,
                      })}
                    </Text>
                  </Card>
                </Col>
              </Row>

              {/* Trends Chart */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24}>
                  <Card title={t('analytics.trends')} extra={<LineChartOutlined />}>
                    {trends.length > 0 ? (
                      <div style={{ padding: 20 }}>
                        <Row gutter={[16, 16]}>
                          {trends.slice(-7).map((day, idx) => (
                            <Col key={idx} xs={24} sm={12} md={6} lg={3}>
                              <Card size="small">
                                <Statistic
                                  title={dayjs(day.date).format('MM.DD')}
                                  value={day.revenue}
                                  prefix="₃"
                                  valueStyle={{ fontSize: 14 }}
                                />
                                <Space style={{ fontSize: 12 }}>
                                  <Text>{t('analytics.ticketsCount', { count: day.tickets })}</Text>
                                </Space>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    ) : (
                      <Empty description={t('analytics.noData')} />
                    )}
                  </Card>
                </Col>
              </Row>

              {/* User Distribution */}
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <Card title={t('analytics.userDistribution')}>
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Statistic
                          title={t('analytics.totalUsers')}
                          value={data.userMetrics?.total || 0}
                          prefix={<UserOutlined />}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title={t('analytics.attendees')}
                          value={data.userMetrics?.attendees || 0}
                          valueStyle={{ color: '#1890ff' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title={t('analytics.speakers')}
                          value={data.userMetrics?.speakers || 0}
                          valueStyle={{ color: '#52c41a' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title={t('analytics.organizers')}
                          value={data.userMetrics?.organizers || 0}
                          valueStyle={{ color: '#722ed1' }}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title={t('analytics.platformActivity')}>
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Statistic
                          title={t('analytics.sessions')}
                          value={data.engagementMetrics?.sessions || 0}
                          prefix={<ClockCircleOutlined />}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title={t('analytics.sponsors')}
                          value={data.engagementMetrics?.sponsors || 0}
                          prefix={<TrophyOutlined />}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title={t('analytics.notifications')}
                          value={data.engagementMetrics?.notifications || 0}
                          prefix={<BellOutlined />}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title={t('analytics.chatMessages')}
                          value={data.chatMetrics?.totalMessages || 0}
                          prefix={<MessageOutlined />}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {view === 'events' && (
            <Card title={t('analytics.eventPerformance')}>
              <Table
                columns={eventColumns}
                dataSource={data.eventMetrics || []}
                rowKey="id"
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => t('analytics.paginationTotal', { count: total }),
                }}
                scroll={{ x: 800 }}
              />
            </Card>
          )}

          {view === 'tickets' && (
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={8}>
                    <Card>
                      <Statistic
                        title={t('analytics.totalSoldTickets')}
                        value={data.ticketMetrics?.totalSold || 0}
                        prefix={<TagsOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Card>
                      <Statistic
                        title={t('analytics.grandTotalRevenue')}
                        value={formatCurrency(data.ticketMetrics?.totalRevenue || 0)}
                        prefix={<DollarOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Card>
                      <Statistic
                        title={t('analytics.averagePrice')}
                        value={formatCurrency(data.ticketMetrics?.averagePrice || 0)}
                        prefix={<RiseOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>
              </Col>
              <Col xs={24}>
                <Card title={t('analytics.ticketTypeDistribution')}>
                  <Table
                    columns={ticketTypeColumns}
                    dataSource={data.ticketMetrics?.distribution || []}
                    rowKey="type"
                    pagination={false}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {view === 'engagement' && (
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <Card title={t('analytics.chatMetrics')}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                      <Statistic
                        title={t('analytics.totalMessages')}
                        value={data.chatMetrics?.totalMessages || 0}
                        prefix={<MessageOutlined />}
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <Statistic
                        title={t('analytics.uniqueUsers')}
                        value={data.chatMetrics?.uniqueUsers || 0}
                        prefix={<UserOutlined />}
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <Statistic
                        title={t('analytics.avgMessagesPerUser')}
                        value={data.chatMetrics?.avgMessagesPerUser || 0}
                        prefix={<BarChartOutlined />}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col xs={24}>
                <Card title={t('analytics.platformComponents')}>
                  <Row gutter={[16, 16]}>
                    <Col xs={12} sm={6}>
                      <Card>
                        <Statistic
                          title={t('analytics.sessions')}
                          value={data.engagementMetrics?.sessions || 0}
                          prefix={<ClockCircleOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card>
                        <Statistic
                          title={t('analytics.speakers')}
                          value={data.engagementMetrics?.speakers || 0}
                          prefix={<SoundOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card>
                        <Statistic
                          title={t('analytics.sponsors')}
                          value={data.engagementMetrics?.sponsors || 0}
                          prefix={<TrophyOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card>
                        <Statistic
                          title={t('analytics.notifications')}
                          value={data.engagementMetrics?.notifications || 0}
                          prefix={<BellOutlined />}
                        />
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </div>
  );
};

export default Analytics;
